import { environment } from 'src/environments/environment';

/** localStorage key holding the uid that asked for demo mode. */
const DEMO_KEY = 'cockpit.demo';

/**
 * DEV-only demo toggle. `?demo=1` swaps the cockpit onto an in-memory store
 * seeded with `SEED_CARDS` (every card type/status, all actions work locally),
 * so the redesigned UI can be exercised end-to-end without real inbound emails.
 * `?demo=0` turns it back off; the choice persists in localStorage so it
 * survives in-app navigation. NEVER active in production.
 *
 * #270 — the persisted flag is stamped with the UID that enabled it, and only
 * that user gets the seeded store back. It used to be a bare `'1'` scoped to the
 * origin alone, so a single `?demo=1` on dev.propor.io stuck to the BROWSER: the
 * next account signed in there — typically a brand-new one finishing onboarding,
 * whose cockpit must be empty — opened onto somebody else's fixtures with no
 * indication they were fake. A stale flag (another uid, or the legacy `'1'`) is
 * dropped on sight rather than merely ignored, so an already-poisoned browser
 * heals on its next load. Unknown uid (auth not resolved yet) reads as OFF but
 * keeps the flag: showing real data early is recoverable, showing fake data as
 * real is not.
 *
 * Lives in its own file (not `cockpit-store.provider.ts`) so that
 * `cockpit-mock-store.preload.ts` can import it without creating a module
 * cycle back to the provider that in turn depends on the preload holder.
 */
export function isCockpitDemoMode(currentUserId: string | null): boolean {
  if (environment.production) return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('demo')) {
      const on = params.get('demo') !== '0' && params.get('demo') !== 'false';
      if (on && currentUserId) localStorage.setItem(DEMO_KEY, currentUserId);
      else localStorage.removeItem(DEMO_KEY);
      // An explicit `?demo=1` is honoured for this load even before auth
      // resolves — it just doesn't persist until there is a uid to stamp.
      return on;
    }
    const owner = localStorage.getItem(DEMO_KEY);
    if (!owner) return false;
    if (!currentUserId) return false;
    if (owner !== currentUserId) {
      localStorage.removeItem(DEMO_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}
